import { Box } from "@mui/material";
import { type FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TagChip } from "../../../../../components/UIToolkit/Badges/TagChip";
import { SystemAutoComplete, type SystemAutoCompleteOption } from "../../../../../components/UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { SystemValidationTextField } from "../../../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { InlineHint } from "../../../../../components/UIToolkit/molecules/InlineHint";
import SystemButton from "../../../../../components/UIToolkit/SystemButton/SystemButton";
import { useAnalytics } from "../../../../../contexts/analytics/hooks";
import { useDebouncedValue } from "../../../../../hooks/useDebouncedValue";
import { FONT_SIZE, INK, TEXT_FIELD_WIDTH } from "../../../../../theme/tokens";
import type { NursaFacility } from "../../../../../utils/models";
import { NURSA_FACILITY_SEARCH_MIN_TERM_LENGTH, useNursaFacilities, useSearchNursaFacilities } from "../hooks";

/** The input, kept stable across the support and non-support paths — a Playwright check types into it. */
const FACILITY_INPUT_TEST_ID = "dept-config-nursa-facility-id";

/**
 * How long the box must be still before a keystroke becomes a Nursa call.
 *
 * 400ms, between the app's two established settings: 350ms where the query is local
 * (`useMonthlyViewState`) and 700ms where every call is a heavy server round trip
 * (`CreateShiftContent`). This one is a light vendor read behind a support-only route, and the
 * operator is typing a building name they already know rather than exploring.
 */
const SEARCH_DEBOUNCE_MS = 400;

/** What the LIST shows between a facility's headline text and its id. Never what gets stored. */
const LABEL_SEPARATOR = " · ";

/**
 * What the row LEADS with: the name, else the backend's assembled descriptor, else the bare id.
 *
 * Strictly ordered, never combined. A facility that has a name shows its name — cramming
 * "Riverside Care · Cleveland, OH · NUR-123" into one 36px row would bury all three. The descriptor
 * exists for the rows Nursa gives no name for (its sandbox gives none at all), which is exactly where
 * a bare UUID told the operator nothing.
 */
const facilityPrimaryText = ({ facilityId, name, description }: NursaFacility): string => name?.trim() || description?.trim() || facilityId;

/**
 * The option's `label` — the row's DISPLAY form, and the text `resolveCommittedTextToFacilityId`
 * accepts verbatim as the "headline · id" form.
 *
 * Only the headline and the id: the descriptor is searchable via `facilitySearchText` below rather
 * than through this string, because this one is also what MUI would echo into the box and what the
 * resolver exact-matches. A facility with no headline of its own is its id, and appending the id to
 * itself would read as "NUR-123 · NUR-123".
 */
const facilityLabel = (facility: NursaFacility): string => {
	const primary = facilityPrimaryText(facility);
	return primary === facility.facilityId ? facility.facilityId : `${primary}${LABEL_SEPARATOR}${facility.facilityId}`;
};

/** Case, padding and stray inner spacing are noise when deciding whether two strings name the same thing. */
const normalize = (text: string): string => text.trim().toLowerCase().replace(/\s+/g, " ");

/**
 * Everything an operator might type to find this row — name, descriptor AND id, whether or not they
 * are on screen.
 *
 * Needed because MUI's default filter reads `getOptionLabel`, which deliberately carries only the
 * headline and the id (above). A named facility's descriptor would then be unsearchable, and
 * "Cleveland" is exactly the kind of thing support types when they know the building, not its name.
 */
const facilitySearchText = ({ facilityId, name, description }: NursaFacility): string => normalize([name, description, facilityId].join(" "));

/**
 * The text the operator committed → the facility id to STORE.
 *
 * Why this exists: the field is `freeSolo`, which forces MUI's `autoSelect`, whose blur handler commits
 * the literal text in the box when no option is keyboard-highlighted. So the operator who types
 * "Riverside Care" (exactly what the placeholder invites), sees the list narrow to one row, and clicks
 * **Save** without clicking the row first would otherwise persist the string `"Riverside Care"` as the
 * facility id — and `get_department_by_nursa_facility_id` matches ids with `==`, so the integration would
 * be silently dead. That is the failure this whole ticket exists to prevent.
 *
 * Three tiers, and the order is the safety:
 * 1. **Anything the roster already lists under this text is an id** — stored exactly as typed, never
 *    re-cased. This is also every genuine pick from the list, whose option `value` IS the bare id.
 * 2. **Exactly one facility answers to this text** — by the headline the list showed it under (its name,
 *    or the descriptor standing in for a missing name), or by the whole `"headline · id"` form — so the
 *    text is a label and we store that facility's id. A descriptor counts here for the same reason a name
 *    does: it is what the row DISPLAYED, so it is what an operator retypes, and storing it verbatim would
 *    be the same silently dead integration.
 * 3. **Anything else stands as typed.** Manual entry is a hard contract (a Nursa outage must never block
 *    configuration, and pasting an id the roster doesn't list has to keep working), so unknown text is
 *    stored, not rejected.
 *
 * Deliberately NOT here: prefix/substring resolution, and MUI's `autoHighlight` (which would make blur
 * commit the first *filtered* option). Both turn a partial input into a confident guess, and guessing the
 * WRONG facility is strictly worse than storing an unknown id — `unknownId` warns about the latter and
 * nothing at all catches the former. A partial or ambiguous input therefore falls through to tier 3 on
 * purpose. Duplicate names fall through for the same reason; the `"Name · id"` form is how an operator
 * disambiguates them.
 */
const resolveCommittedTextToFacilityId = (committed: string, facilities: NursaFacility[]): string => {
	const trimmed = committed.trim();
	const normalized = normalize(trimmed);

	if (facilities.some((facility) => normalize(facility.facilityId) === normalized)) return trimmed;

	const labelled = facilities.filter((facility) => {
		const primary = facilityPrimaryText(facility);
		// A facility whose headline IS its id contributes nothing here — tier 1 already answered that text.
		if (primary === facility.facilityId) return false;
		return normalize(primary) === normalized || normalize(facilityLabel(facility)) === normalized;
	});
	const [onlyLabelled] = labelled;
	return labelled.length === 1 && onlyLabelled ? onlyLabelled.facilityId : trimmed;
};

type Props = {
	/** The id as it is stored on the form — the single source of truth for what this control shows. */
	facilityId: string | null;
	/** `null` clears the mapping. The caller owns the write; this never writes from the fetched list. */
	onFacilityIdChange: (facilityId: string | null) => void;
	/** Listing facilities is support-only. Everyone else gets today's plain typed field, unchanged. */
	isSupport: boolean;
	/** Used only to tell "another department already claims this facility" from "this one does". */
	departmentId: string;
};

/**
 * The department's Nursa Facility ID — picked from Nursa's own list of facilities, by name.
 *
 * The one rule this control is built around: **the fetch DECORATES the field, it never OWNS it.**
 * The value always comes from `facilityId`, the input is always free-typeable (`freeSolo`) and never
 * disabled, and nothing here ever writes a value derived from the query. Only the option list, the
 * in-field spinner and the hint underneath change with the query's state — so a Nursa outage costs
 * the operator a convenience, never the ability to configure the department.
 */
export const NursaFacilityIdField: FC<Props> = ({ facilityId, onFacilityIdChange, isSupport, departmentId }) => {
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();
	// Lazy: the endpoint calls out to Nursa, so it waits until someone actually opens the picker
	// rather than firing on every render of the Integrations tab.
	const [hasOpenedPicker, setHasOpenedPicker] = useState(false);
	const { data, error, isFetching, refetch } = useNursaFacilities({ enabled: isSupport && hasOpenedPicker });

	/**
	 * The text in the box is controlled, and it is NOT MUI's to set.
	 *
	 * Left uncontrolled, selecting (or merely blurring onto) a highlighted option makes MUI write that
	 * option's label into the box — the rich "Name · id" — while our `value` prop, derived from the
	 * unchanged stored id, does not change. MUI's own re-sync effect bails out under `freeSolo` when
	 * the value prop is identical, so the rich label STAYS in the box, and the next blur commits it
	 * verbatim as the facility id. Measured, not theorised: the regression spec reproduces it.
	 *
	 * Declared above the option lists because the directory search below is derived from it.
	 */
	const [inputText, setInputText] = useState(facilityId ?? "");
	// The stored id is the only thing that may put text in the box — including after a pick, and after
	// a discard/reload replaces the form's value under us.
	useEffect(() => setInputText(facilityId ?? ""), [facilityId]);

	/**
	 * What the operator TYPED, which is not the same as what is in the box.
	 *
	 * With a facility already mapped the box reads as the stored id, and that id is well over the
	 * minimum length — so without this comparison, merely opening the picker on a configured
	 * department would fire a directory search for a UUID. Text equal to the stored value is not a
	 * query; it is the control at rest.
	 */
	const typedTerm = inputText.trim() === (facilityId ?? "").trim() ? "" : inputText.trim();
	const debouncedTerm = useDebouncedValue(typedTerm, SEARCH_DEBOUNCE_MS);
	const hasSearchTerm = debouncedTerm.length >= NURSA_FACILITY_SEARCH_MIN_TERM_LENGTH;
	const { data: searchData, isFetching: isSearching } = useSearchNursaFacilities(debouncedTerm, { enabled: isSupport && hasOpenedPicker });

	// One event per settled search rather than per keystroke: the term is already debounced, and
	// `searchData` only changes when a response lands.
	useEffect(() => {
		if (!hasSearchTerm || !searchData) return;
		track("dept_config_nursa_facility_searched", {
			department_id: departmentId,
			// The term itself is not sent: it is a building name the operator typed, and the useful
			// question is whether the directory answered, not what they were looking for.
			term_length: debouncedTerm.length,
			status: searchData.status,
			result_count: searchData.facilities.length,
			has_more: searchData.hasMore,
		});
	}, [hasSearchTerm, searchData, debouncedTerm, departmentId, track]);

	useEffect(() => {
		if (!error) return;
		trackError("fetch_nursa_facilities", {
			error_message: error instanceof Error ? error.message : String(error),
			department_id: departmentId,
		});
	}, [error, trackError, departmentId]);

	// An unloaded list is an empty list of OPTIONS — never an empty answer about the stored id, which
	// is why every hint below keys off `status`/`error` rather than off `facilities.length`.
	const rosterFacilities = useMemo(() => data?.facilities ?? [], [data]);
	const searchFacilities = useMemo(() => searchData?.facilities ?? [], [searchData]);
	/**
	 * The directory replaces the list only when it actually answered.
	 *
	 * A failed or still-loading search falls back to the connected roster rather than blanking the
	 * list, which is the same contract the roster itself has: Nursa being unreachable costs the
	 * operator a convenience, never the ability to configure the department.
	 */
	const isShowingSearchResults = hasSearchTerm && searchData?.status === "ok";
	const facilities = useMemo(
		() => (isShowingSearchResults ? searchFacilities : rosterFacilities),
		[isShowingSearchResults, searchFacilities, rosterFacilities],
	);
	// The rich label is only ever shown in the LIST — see `selectedOption`.
	const facilityOptions = useMemo<SystemAutoCompleteOption<string>[]>(
		() => facilities.map((facility) => ({ label: facilityLabel(facility), value: facility.facilityId })),
		[facilities],
	);
	/**
	 * BOTH populations, deliberately — this map answers "what do we know about this id?", not "what is
	 * in the list right now".
	 *
	 * If it tracked only the visible options, typing a search term would make `matchedFacility` for
	 * the ALREADY-SAVED id disappear, and `showUnknownIdWarning` below would accuse a perfectly good
	 * mapping of being unknown while the operator was merely looking at something else.
	 *
	 * Roster entries are written last so they win a collision: a facility in both carries a richer
	 * descriptor from the roster (health system and locality) than the directory's city and state.
	 */
	const facilityById = useMemo(
		() => new Map([...searchFacilities, ...rosterFacilities].map((facility) => [facility.facilityId, facility])),
		[searchFacilities, rosterFacilities],
	);
	// The facilities Nursa will actually let us act on. A directory result outside this set is real,
	// and mappable, but posting to it would be refused until Nursa connects it to our account.
	const connectedFacilityIds = useMemo(() => new Set(rosterFacilities.map((facility) => facility.facilityId)), [rosterFacilities]);
	// Deduped, because `resolveCommittedTextToFacilityId`'s "exactly one facility answers to this
	// text" tier counts matches: the same facility present in both lists would look ambiguous and
	// fall through to being stored as raw text.
	const knownFacilities = useMemo(() => [...facilityById.values()], [facilityById]);
	// Precomputed per facility, not per keystroke: this is the haystack every typed character re-scans.
	const searchTextById = useMemo(() => new Map(facilities.map((facility) => [facility.facilityId, facilitySearchText(facility)])), [facilities]);
	const matchedFacility = facilityId ? facilityById.get(facilityId) : undefined;

	/**
	 * The RAW id, never the rich "Name · id" option label — the input must show what is STORED.
	 *
	 * Not cosmetic: `freeSolo` forces MUI's `autoSelect`, whose blur handler commits the literal text
	 * in the input through `onChange`. Showing the rich label there would therefore write
	 * "Name · NUR-123" into the facility id the first time the operator clicked away. (Same reason
	 * `HrisSourceCard` shows the raw dept code.) An id absent from `options` survives regardless:
	 * `SystemAutoComplete` compares options to the value by `.value` only.
	 */
	// Memoised for identity, not cost: MUI compares this prop by reference to decide whether the value
	// changed, and a fresh object every render makes it re-assert the input's text on every render.
	const selectedOption = useMemo(() => (facilityId ? { label: facilityId, value: facilityId } : null), [facilityId]);

	const status = data?.status;
	// An HTTP failure and a reported `nursa_error` are the same thing to the operator: the list is
	// unavailable and the field is still theirs to fill in.
	const loadFailed = !!error || status === "nursa_error";
	const showUnknownIdWarning = status === "ok" && !!facilityId && !matchedFacility;
	const unusableRowCount = data?.unusableRowCount ?? 0;
	// Independent of `status`: a truncated roster still comes back `ok`, and the list being a PREFIX of
	// reality is exactly the case where "my facility isn't here" is the wrong conclusion to draw.
	const isTruncated = !!data?.truncated;
	// Not one row has a name OR a descriptor, so the list really is a column of ids and no amount of
	// layout can fix that. Saying so is the honest version of it — an operator who sees only ids should
	// know the list is complete and Nursa sent nothing to show, rather than assume the picker is broken.
	// Gated on the DESCRIPTOR too: once a row can say "Heritage Health · Cleveland, OH", the list is no
	// longer ids-only and the hint would be telling the operator something contradicted by what they see.
	// Scoped to the ROSTER on purpose: it reports what our own account's facilities look like, and a
	// directory page of named buildings must neither raise it nor clear it.
	const showNamesMissingHint =
		status === "ok" && rosterFacilities.length > 0 && rosterFacilities.every((facility) => facilityPrimaryText(facility) === facility.facilityId);

	const handleChange = (_event: unknown, option: SystemAutoCompleteOption<string> | null) => {
		// Under `autoSelect` this `option` is just as likely to be the raw TEXT in the box (wrapped as
		// `{label, value}` by `SystemAutoComplete`) as a picked option.
		const committedText = option ? option.value : null;
		// `autoSelect` re-commits that text on EVERY blur — including the blur that follows merely
		// OPENING the picker and clicking away without touching anything. The box is controlled to read
		// as the stored id, so a commit equal to it is not an edit, and returning here keeps an idle
		// open/blur a complete no-op: no write, no analytics event, no dirtied form.
		//
		// Compared BEFORE resolution, because resolution is exactly what makes the idle blur unsafe: a
		// legacy NAME-shaped stored id ("Riverside Care" — the broken state this ticket exists to fix)
		// resolves to that facility's real id, which the post-resolve guard below would then read as a
		// genuine change and write a re-mapping the operator never asked for.
		if (committedText === facilityId) return;
		// Trimmed on the way into form state, not just when comparing: every consumer matches this id
		// exactly (inbound webhook resolution, and Nursa's `facilities` array), so a pasted id carrying
		// a space would map to nothing and read as a dead integration.
		// Resolved so that a typed facility NAME lands as that facility's id, not as the name.
		// DEFAULT: an all-whitespace id is absence, not a value, so it stores as NULL.
		const nextFacilityId = committedText === null ? null : resolveCommittedTextToFacilityId(committedText, knownFacilities) || null;
		// The other half of "only a real change writes": text that DIFFERS from the stored value but
		// resolves back to it (typing the name of the facility already mapped) is not a change either.
		if (nextFacilityId === facilityId) return;
		onFacilityIdChange(nextFacilityId);
		track("dept_config_nursa_facility_selected", {
			department_id: departmentId,
			// Whether the list did its job, or the operator fell back to typing.
			picked_from_list: !!nextFacilityId && facilityById.has(nextFacilityId),
		});
	};

	if (!isSupport) {
		// Byte-identical to the pre-picker field: no query, no hints, no list — the facility roster is
		// support-only, and a combobox with nothing in it would just be a worse text box.
		return (
			<SystemValidationTextField
				value={facilityId ?? ""}
				onChange={(event) => onFacilityIdChange(event.target.value.trim() || null)}
				placeholder={t("department.config.integrations.nursa.facilityId.placeholder")}
				formControlSx={{ width: TEXT_FIELD_WIDTH.IDENTIFIER }}
				injectedProps={{
					slotProps: {
						htmlInput: { "data-testid": FACILITY_INPUT_TEST_ID, "aria-label": t("department.config.integrations.nursa.facilityId.label") },
					},
				}}
				dataTestId="dept-config-nursa-facility-id-field"
			/>
		);
	}

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
			<SystemAutoComplete<string>
				// The manual-entry escape hatch, and the reason nothing below ever disables the control:
				// typed text commits on blur whether or not Nursa answered.
				freeSolo
				// `freeSolo` hides MUI's chevron by default, which would leave a picker with no way to
				// BROWSE — only to type. With a saved id in the box the list still opens unfiltered
				// (MUI skips filtering while the input still reads as the selected value), so the
				// chevron is how an operator sees what Nursa offers without first guessing a name.
				forcePopupIcon
				fullWidth
				// Either read fills the list, so either one is worth a spinner.
				loading={isFetching || isSearching}
				options={facilityOptions}
				value={selectedOption}
				onChange={handleChange}
				inputValue={inputText}
				onInputChange={(_event, value, reason) => {
					// Only the operator's own edits may put text in the box: `input` (typing) and `clear` (the
					// × button).
					if (reason === "input" || reason === "clear") {
						setInputText(value);
						return;
					}
					// Everything else is MUI echoing a selected option's rich "Name · id" LABEL back into the
					// box — `reset` when it re-syncs (including Escape), `blur` when `autoSelect` commits the
					// highlighted option. The box must read as what is STORED, so those re-assert the stored
					// id instead. (After a genuine pick this lands on the OLD id for one render, because MUI
					// resets the input before it reports the new value; the effect above then catches up.)
					setInputText(facilityId ?? "");
				}}
				onOpenStateChange={(_event, _reason, isOpen) => {
					if (isOpen) setHasOpenedPicker(true);
				}}
				getOptionEndAdornment={(option) => {
					const facility = facilityById.get(option.value);
					// Only a claim by SOMEONE ELSE is worth flagging; this department's own facility is
					// the expected state. Annotated rather than disabled — support re-points facilities.
					const takenByAnother = !!facility?.takenByDepartmentId && facility.takenByDepartmentId !== departmentId;
					// Only while browsing the DIRECTORY: every row of the connected roster is connected by
					// definition, so the chip would be noise there rather than information.
					const notConnected = isShowingSearchResults && !connectedFacilityIds.has(option.value);
					if (!takenByAnother && !notConnected) return null;
					return (
						<Box sx={{ display: "flex", gap: 0.5, alignItems: "center", flexShrink: 0 }}>
							{notConnected && (
								<TagChip
									label={t("department.config.integrations.nursa.facilityId.search.notConnected")}
									tooltip={t("department.config.integrations.nursa.facilityId.search.notConnected.tooltip")}
									dataTestId={`dept-config-nursa-facility-not-connected-${option.value}`}
								/>
							)}
							{takenByAnother && (
								<TagChip
									label={t("department.config.integrations.nursa.facilityId.taken")}
									tooltip={t("department.config.integrations.nursa.facilityId.taken.tooltip")}
									dataTestId={`dept-config-nursa-facility-taken-${option.value}`}
								/>
							)}
						</Box>
					);
				}}
				// Browsing is the point of this list: the operator opens it WITH an id already mapped,
				// precisely to read the alternatives. Fading them to 50% (the shared default, which suits a
				// list you confirm rather than one you shop in) dims exactly the names they came to read.
				dimUnselectedOptions={false}
				// Everything the operator might type, not just what the row shows: the label carries the
				// headline and the id, so without this a named facility's descriptor ("Cleveland") — the
				// thing support types when they know the building rather than its name — matches nothing.
				filterOptions={(options, { inputValue }) => {
					// Nursa already applied the term, against fields we never received. Filtering again locally
					// would silently drop rows the server deliberately returned -- and it would re-filter a
					// DEBOUNCED list with the LIVE input, emptying the list between the last keystroke and the
					// response landing.
					if (isShowingSearchResults) return options;
					const query = normalize(inputValue);
					if (!query) return options;
					return options.filter((option) => (searchTextById.get(option.value) ?? normalize(option.label)).includes(query));
				}}
				/**
				 * Two weights, not one string: the HEADLINE is what an operator recognises (the name, or the
				 * descriptor standing in for a missing one) and the id is the opaque thing it stands for, so
				 * the headline leads at body size in primary ink and the id trails muted and a notch smaller.
				 * A facility with neither promotes its id to the headline and renders NO second element — an
				 * empty muted slot (or an invented "Unnamed") would claim the roster told us something it didn't.
				 *
				 * The separator stays part of the id's own text so the row still reads `headline · id`
				 * end-to-end — the form `resolveCommittedTextToFacilityId` accepts verbatim, and the signal
				 * `nursa-facility-picker.spec.ts` uses to tell a labelled row from a bare-id one.
				 */
				renderOptionLabel={(option, { isSelected }) => {
					const facility = facilityById.get(option.value);
					// An option with no facility behind it cannot happen (the options ARE the facilities), but
					// the id is the honest headline if it ever did.
					const primary = facility ? facilityPrimaryText(facility) : option.value;
					return (
						<Box sx={{ display: "flex", alignItems: "baseline", flex: "1 1 auto", minWidth: 0, overflow: "hidden" }}>
							<Box
								component="span"
								title={primary}
								data-testid={`dept-config-nursa-facility-row-name-${option.value}`}
								sx={{
									overflow: "hidden",
									whiteSpace: "nowrap",
									textOverflow: "ellipsis",
									minWidth: 0,
									fontSize: FONT_SIZE.EMPHASIS,
									fontWeight: isSelected ? 600 : 500,
									color: INK.PRIMARY,
								}}
							>
								{primary}
							</Box>
							{primary !== option.value && (
								<Box
									component="span"
									data-testid={`dept-config-nursa-facility-row-id-${option.value}`}
									// `pre` keeps the separator's own spaces, so the row reads exactly as the label does.
									sx={{ flexShrink: 0, whiteSpace: "pre", fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED }}
								>
									{`${LABEL_SEPARATOR}${option.value}`}
								</Box>
							)}
						</Box>
					);
				}}
				getOptionDataTestId={(option) => `dept-config-nursa-facility-option-${option.value}`}
				dataTestId="dept-config-nursa-facility-id-autocomplete"
				renderInput={(params) => (
					<SystemValidationTextField
						isInsideAutoComplete
						fullWidth
						reserveHeightForHelperText={false}
						placeholder={t("department.config.integrations.nursa.facilityId.picker.placeholder")}
						// `params` merged by hand: the shared component's default `renderInput` spreads
						// `textFieldProps` AFTER `injectedProps`, so routing a test id through that prop would
						// replace `params` wholesale and unwire the input from the autocomplete.
						injectedProps={{
							...params,
							slotProps: {
								...params.slotProps,
								htmlInput: {
									...params.slotProps.htmlInput,
									"data-testid": FACILITY_INPUT_TEST_ID,
									// The field's visible label lives on the enclosing `SettingsSection`, so without
									// this the control announces itself as a bare combobox.
									"aria-label": t("department.config.integrations.nursa.facilityId.label"),
								},
							},
						}}
					/>
				)}
			/>

			{matchedFacility?.name?.trim() ? (
				<InlineHint tone="success" dataTestId="dept-config-nursa-facility-matched">
					{t("department.config.integrations.nursa.facilityId.matched", { name: matchedFacility.name })}
				</InlineHint>
			) : (
				// Same test id, different sentence: the confirmation an operator needs is "the id in the box
				// is the building I meant", and for a nameless facility the descriptor is the only thing that
				// can say so. Worded as a description rather than a name, because it is not one — the field
				// must never imply Nursa named a facility it didn't.
				!!matchedFacility?.description?.trim() && (
					<InlineHint tone="success" dataTestId="dept-config-nursa-facility-matched">
						{t("department.config.integrations.nursa.facilityId.matchedDescription", { description: matchedFacility.description })}
					</InlineHint>
				)
			)}

			{isShowingSearchResults && searchData?.hasMore && (
				// Nursa reports no total, so this is honestly "there may be more" and never a count. Saying
				// nothing would let an operator conclude their building is absent when it is simply on page 2 —
				// and there is no page 2 in this control by design: refining the term is the cheaper answer.
				<InlineHint tone="info" dataTestId="dept-config-nursa-facility-search-more">
					{t("department.config.integrations.nursa.facilityId.search.more")}
				</InlineHint>
			)}

			{hasSearchTerm && searchData?.status === "no_facilities" && (
				// Distinct from MUI's bare "No options": that reads as a broken control, this says the
				// directory answered and had nothing, and that typing an id by hand still works.
				<InlineHint tone="neutral" dataTestId="dept-config-nursa-facility-search-empty">
					{t("department.config.integrations.nursa.facilityId.search.empty")}
				</InlineHint>
			)}

			{hasSearchTerm && (searchData?.status === "nursa_error" || searchData?.status === "credentials_missing") && (
				// The list has already fallen back to the connected roster, so this explains why the results
				// narrowed rather than leaving the operator to think the directory returned three buildings.
				<InlineHint tone="warning" dataTestId="dept-config-nursa-facility-search-failed">
					{t("department.config.integrations.nursa.facilityId.search.failed")}
				</InlineHint>
			)}

			{showUnknownIdWarning && (
				// Never silently corrected or cleared: an id Nursa doesn't list may still be the right one
				// (a facility added since, a roster we only partly see), and it is what is saved.
				<InlineHint tone="warning" dataTestId="dept-config-nursa-facility-unknown-id">
					{t("department.config.integrations.nursa.facilityId.unknownId")}
				</InlineHint>
			)}

			{loadFailed && (
				<InlineHint
					tone="warning"
					dataTestId="dept-config-nursa-facility-load-failed"
					actionPlacement="below"
					action={
						<SystemButton buttonVariant="borderless" size="small" onClick={() => void refetch()} data-testid="dept-config-nursa-facility-load-retry">
							{t("retry")}
						</SystemButton>
					}
				>
					{t("department.config.integrations.nursa.facilityId.loadFailed")}
				</InlineHint>
			)}

			{status === "credentials_missing" && (
				<InlineHint tone="warning" dataTestId="dept-config-nursa-facility-credentials-missing">
					{t("department.config.integrations.nursa.facilityId.credentialsMissing")}
				</InlineHint>
			)}

			{status === "no_facilities" && !isTruncated && (
				// Suppressed while truncated: a fetch we abandoned early can yield zero usable rows, and
				// "Nursa has no facilities for our account" is then a claim the data cannot support. The
				// truncation hint below is the accurate one — and it carries the same manual-entry escape
				// hatch — so the operator is never told the roster is empty AND partially shown at once.
				<InlineHint tone="neutral" dataTestId="dept-config-nursa-facility-empty">
					{t("department.config.integrations.nursa.facilityId.empty")}
				</InlineHint>
			)}

			{showNamesMissingHint && (
				<InlineHint tone="neutral" dataTestId="dept-config-nursa-facility-names-missing">
					{t("department.config.integrations.nursa.facilityId.namesMissing")}
				</InlineHint>
			)}

			{isTruncated && (
				// Co-renders with every hint but `empty` on purpose: "we stopped reading Nursa's roster early"
				// is a different fact from "this id isn't in the list", and an operator who sees only the
				// latter would wrongly conclude their facility isn't on our account.
				<InlineHint tone="warning" dataTestId="dept-config-nursa-facility-truncated">
					{t("department.config.integrations.nursa.facilityId.truncated")}
				</InlineHint>
			)}

			{unusableRowCount > 0 && (
				<InlineHint tone="warning" dataTestId="dept-config-nursa-facility-unusable-rows">
					{t("department.config.integrations.nursa.facilityId.unusableRows", { count: unusableRowCount })}
				</InlineHint>
			)}
		</Box>
	);
};
